import { eventOperationErrorMessages } from 'src/features/payment/utils/eventOperationErrorMessages';
import { eventTypeMessages } from 'src/features/payment/utils/eventTypeMessages';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import {
    PAYMENT_SOURCE_TYPE,
    PAYMENT_TYPE,
    PAY_STATION_STATUS,
    PayStationEventAPI,
    PIPELINE_PROFILE,
    PAY_STATION_UI_MODAL,
    PayStationPaymentType,
    PAYMENT_ITEM_TYPE,
    PAYMENT_SERVICE_TYPE,
} from 'src/features/payment/utils/types';
import { BadgeOption, BadgeOptions } from 'src/registry/types/badges';
import { ICON_MAP } from 'src/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { statisticMessages } from 'src/features/statistic/utils/messages';

export const PAYMENTS_SIDE_PANEL = 'payments-side-panel';

export enum PayStationOperationType {
    STAY_PAYMENT_CASH = 'stay-payment-cash',
    STAY_PAYMENT_CARD = 'stay-payment-card',
    MANUAL_ACCEPT = 'manual-accept',
    MANUAL_DISPENSE_COINS = 'manual-dispense-coins',
    MANUAL_DISPENSE_BANKNOTES = 'manual-dispense-banknotes',
    SYNCHRONISATION = 'synchronisation',
    CLEAR_COINS = 'clear-coins',
    CLEAR_BANKNOTES = 'clear-banknotes',
    EMPTY_RECYCLE_BOXES = 'empty-recycle-boxes',
    SUBSCRIPTION_PAYMENT_CARD = 'subscription-payment-card',
    SUBSCRIPTION_PAYMENT_CASH = 'subscription-payment-cash',
}

export const payStationOperationTypeBadgeOptions: BadgeOptions = {
    [PayStationOperationType.STAY_PAYMENT_CASH]: {
        label: paymentsMessages.stayCashPayment,
        theme: 'yellow',
    },
    [PayStationOperationType.STAY_PAYMENT_CARD]: {
        label: paymentsMessages.stayCardPayment,
        theme: 'sky',
    },
    [PayStationOperationType.MANUAL_ACCEPT]: {
        label: paymentsMessages.manualAccept,
        theme: 'green',
    },
    [PayStationOperationType.MANUAL_DISPENSE_COINS]: {
        label: paymentsMessages.manualDispenseCoins,
        theme: 'teal',
    },
    [PayStationOperationType.MANUAL_DISPENSE_BANKNOTES]: {
        label: paymentsMessages.manualDispenseBanknotes,
        theme: 'emerald',
    },
    [PayStationOperationType.SYNCHRONISATION]: {
        label: paymentsMessages.synchronisation,
        theme: 'rose',
    },
    [PayStationOperationType.CLEAR_COINS]: {
        label: paymentsMessages.clearCoins,
        theme: 'green',
    },
    [PayStationOperationType.CLEAR_BANKNOTES]: {
        label: paymentsMessages.clearBanknotes,
        theme: 'red',
    },
    [PayStationOperationType.EMPTY_RECYCLE_BOXES]: {
        label: paymentsMessages.emptyRecycleBoxes,
        theme: 'indigo',
    },
    [PayStationOperationType.SUBSCRIPTION_PAYMENT_CARD]: {
        label: paymentsMessages.subscriptionPaymentCard,
        theme: 'orange',
    },
    [PayStationOperationType.SUBSCRIPTION_PAYMENT_CASH]: {
        label: paymentsMessages.subscriptionPaymentCash,
        theme: 'red',
    },
};

export enum PayStationDeviceType {
    BANKNOTE_VALIDATOR = 0,
    COIN_SELECTOR = 1,
}

export enum PayStationStatus {
    PENDING = 'pending',
    SUCCESS = 'success',
    CANCELED = 'canceled',
    ERROR = 'error',
}

export enum EventCategory {
    EVENT = 'event',
    ERROR = 'error',
}

export enum PayStationStage {
    PENDING = 'pending',
    INSERTING_AMOUNT = 'inserting-amount',
    INSERTING_AMOUNT_COMPLETED = 'inserting-amount-completed',
    INSERTING_AMOUNT_CANCELED = 'inserting-amount-canceled',
    SUCCESSFUL_CARD_TRANSACTION = 'successful-card-transaction',
    FAILED_CARD_TRANSACTION = 'failed-card-transaction',
    REVERSED_CARD_TRANSACTION = 'reversed-card-transaction',
    FAILED_REVERSED_CARD_TRANSACTION = 'failed-reversed-card-transaction',
    CANCELED_CARD_TRANSACTION = 'canceled-card-transaction',
    ISSUING_FISCAL_NOTE = 'issuing-fiscal-note',
    ISSUING_FISCAL_NOTE_COMPLETED = 'issuing-fiscal-note-completed',
    DISPENSING_AMOUNT_CANCELLED_INSERTING_AMOUNT = 'dispense-amount-cancelled-inserting-amount',
    CANCELLED_INSERTING_AMOUNT_RETURNED_AMOUNT_SUCCESSFUL = 'cancelled-inserting-amount-returned-amount-successful',
    CANCELLED_INSERTING_AMOUNT_RETURNED_AMOUNT_FAILED = 'cancelled-inserting-amount-returned-amount-failed',
    DISPENSE_AMOUNT_WITH_ISSUE_FISCAL_NOTE_COMPLETED = 'dispense-amount-with-issue-fiscal-note-completed',
    DISPENSE_AMOUNT_WITH_ISSUE_FISCAL_NOTE_FAILED = 'dispense-amount-with-issue-fiscal-note-failed',
    DISPENSE_AMOUNT_WITH_ISSUE_FISCAL_NOTE = 'dispense-amount-issuing-fiscal-note',
    INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE = 'initialize-reversal-issuing-fiscal-note',
    INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_SUCCESSFUL = 'initialize-reversal-issuing-fiscal-note-reverse-transaction-successful',
    INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_FAILED = 'initialize-reversal-issuing-fiscal-note-reverse-transaction-failed',
    INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_COMPLETED = 'initialize-reversal-issuing-fiscal-note-completed',
    INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_FAILED = 'initialize-reversal-issuing-fiscal-note-failed',
    SYNCHRONISING = 'synchronising',
    CLEARING = 'clearing',
    COMPLETED = 'completed',
    ISSUING_FISCAL_NOTE_FAILED = 'issuing-fiscal-note-failed',
    ISSUING_FISCAL_NOTE_FAILED_RETURN_AMOUNT_SUCCESSFUL = 'issuing-fiscal-note-failed-return-amount-successful',
    ISSUING_FISCAL_NOTE_COMPLETED_RETURN_AMOUNT_SUCCESSFUL = 'issuing-fiscal-note-completed-return-amount-successful',
    ISSUING_FISCAL_NOTE_COMPLETED_RETURN_AMOUNT_FAILED = 'issuing-fiscal-note-completed-return-amount-failed',
    ISSUING_FISCAL_NOTE_FAILED_RETURN_AMOUNT_FAILED = 'issuing-fiscal-note-failed-return-amount-failed',
    ISSUING_FISCAL_NOTE_FAILED_REVERSE_TRANSACTION_SUCCESSFUL = 'issuing-fiscal-note-failed-reverse-transaction-successful',
    ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_SUCCESSFUL = 'issuing-fiscal-note-reverse-transaction-successful',
    ISSUING_FISCAL_NOTE_FAILED_REVERSE_TRANSACTION_FAILED = 'issuing-fiscal-note-failed-reverse-transaction-failed',
    ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_FAILED = 'issuing-fiscal-note-reverse-transaction-failed',
}

export const payStationStageBadgeOptions: BadgeOptions = {
    [PayStationStage.PENDING]: {
        label: paymentsMessages.pending,
        theme: 'yellow',
    },
    [PayStationStage.CLEARING]: {
        label: paymentsMessages.clearing,
        theme: 'sky',
    },
    [PayStationStage.COMPLETED]: {
        label: paymentsMessages.completed,
        theme: 'green',
    },
    [PayStationStage.INSERTING_AMOUNT]: {
        label: paymentsMessages.insertingAmount,
        theme: 'teal',
    },
    [PayStationStage.INSERTING_AMOUNT_COMPLETED]: {
        label: paymentsMessages.insertingAmountCompleted,
        theme: 'emerald',
    },
    [PayStationStage.INSERTING_AMOUNT_CANCELED]: {
        label: paymentsMessages.insertingAmountCanceled,
        theme: 'rose',
    },
    [PayStationStage.SUCCESSFUL_CARD_TRANSACTION]: {
        label: paymentsMessages.successfulCardTransaction,
        theme: 'green',
    },
    [PayStationStage.FAILED_CARD_TRANSACTION]: {
        label: paymentsMessages.failedCardTransaction,
        theme: 'red',
    },
    [PayStationStage.SYNCHRONISING]: {
        label: paymentsMessages.synchronising,
        theme: 'indigo',
    },
    [PayStationStage.REVERSED_CARD_TRANSACTION]: {
        label: paymentsMessages.reversedCardTransaction,
        theme: 'orange',
    },
    [PayStationStage.FAILED_REVERSED_CARD_TRANSACTION]: {
        label: paymentsMessages.failedReversedCardTransaction,
        theme: 'red',
    },
    [PayStationStage.CANCELED_CARD_TRANSACTION]: {
        label: paymentsMessages.canceledCardTransaction,
        theme: 'rose',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE]: {
        label: paymentsMessages.issuingFiscalNote,
        theme: 'purple',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_COMPLETED]: {
        label: paymentsMessages.issuingFiscalNoteCompleted,
        theme: 'violet',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_FAILED]: {
        label: paymentsMessages.issuingFiscalNoteFailed,
        theme: 'red',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_COMPLETED_RETURN_AMOUNT_FAILED]: {
        label: paymentsMessages.issuingFiscalNoteCompletedReturnAmountFailed,
        theme: 'red',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_FAILED_RETURN_AMOUNT_FAILED]: {
        label: paymentsMessages.issuingFiscalNoteFailedReturnAmountFailed,
        theme: 'red',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_FAILED_REVERSE_TRANSACTION_FAILED]: {
        label: paymentsMessages.issuingFiscalNoteFailedReverseTransactionFailed,
        theme: 'red',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_FAILED]: {
        label: paymentsMessages.issuingFiscalNoteReverseTransactionFailed,
        theme: 'red',
    },
    [PayStationStage.INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_FAILED]: {
        label: paymentsMessages.initializeReverseIssuingFiscalNoteFailed,
        theme: 'red',
    },
    [PayStationStage.DISPENSE_AMOUNT_WITH_ISSUE_FISCAL_NOTE_FAILED]: {
        label: paymentsMessages.dispenseAmountWithIssueFiscalNoteFailed,
        theme: 'red',
    },
    [PayStationStage.CANCELLED_INSERTING_AMOUNT_RETURNED_AMOUNT_FAILED]: {
        label: paymentsMessages.cancelledInsertingAmountReturnedAmountFailed,
        theme: 'red',
    },
    [PayStationStage.INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_FAILED]: {
        label: paymentsMessages.initializeReverseIssuingFiscalNoteReverseTransactionFailed,
        theme: 'red',
    },
    [PayStationStage.DISPENSING_AMOUNT_CANCELLED_INSERTING_AMOUNT]: {
        label: paymentsMessages.dispensingAmountCancelledInsertingAmount,
        theme: 'red',
    },
    [PayStationStage.CANCELLED_INSERTING_AMOUNT_RETURNED_AMOUNT_SUCCESSFUL]: {
        label: paymentsMessages.cancelledInsertingAmountReturnedAmountSuccessful,
        theme: 'green',
    },
    [PayStationStage.DISPENSE_AMOUNT_WITH_ISSUE_FISCAL_NOTE_COMPLETED]: {
        label: paymentsMessages.dispenseAmountWithIssueFiscalNoteCompleted,
        theme: 'green',
    },
    [PayStationStage.INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_COMPLETED]: {
        label: paymentsMessages.initializeReversalIssuingFiscalNoteCompleted,
        theme: 'green',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_FAILED_RETURN_AMOUNT_SUCCESSFUL]: {
        label: paymentsMessages.issuingFiscalNoteFailedReturnAmountSuccessful,
        theme: 'green',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_COMPLETED_RETURN_AMOUNT_SUCCESSFUL]: {
        label: paymentsMessages.issuingFiscalNoteCompletedReturnAmountSuccessful,
        theme: 'green',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_FAILED_REVERSE_TRANSACTION_SUCCESSFUL]: {
        label: paymentsMessages.issuingFiscalNoteFailedReverseTransactionSuccessful,
        theme: 'green',
    },
    [PayStationStage.ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_SUCCESSFUL]: {
        label: paymentsMessages.issuingFiscalNoteReverseTransactionSuccessful,
        theme: 'green',
    },
    [PayStationStage.INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE_REVERSE_TRANSACTION_SUCCESSFUL]: {
        label: paymentsMessages.initializeReversalIssuingFiscalNoteReverseTransactionSuccessful,
        theme: 'green',
    },
    [PayStationStage.DISPENSE_AMOUNT_WITH_ISSUE_FISCAL_NOTE]: {
        label: paymentsMessages.dispenseAmountWithIssueFiscalNote,
        theme: 'pink',
    },
    [PayStationStage.INITIALIZE_REVERSAL_ISSUING_FISCAL_NOTE]: {
        label: paymentsMessages.initializeReversalIssuingFiscalNote,
        theme: 'cyan',
    },
};
export const paymentTypeBadgeOptions: BadgeOptions = {
    [PAYMENT_TYPE.CARD]: {
        label: paymentsMessages.card,
        icon: ICON_MAP.CARD,
        theme: 'blue',
    },
    [PAYMENT_TYPE.CASH]: {
        label: paymentsMessages.cash,
        icon: ICON_MAP.CASH,
        theme: 'green',
    },
    [PAYMENT_TYPE.ONLINE]: {
        label: paymentsMessages.online,
        icon: ICON_MAP.ONLINE,
        theme: 'purple',
    },
    [PAYMENT_TYPE.BANK]: {
        label: paymentsMessages.bank,
        icon: ICON_MAP.BANK,
        theme: 'orange',
    },
};

export const paymentByProductTypeTranslationMap: Partial<
    Record<PAYMENT_ITEM_TYPE, { id: string; defaultMessage: string }>
> = {
    [PAYMENT_ITEM_TYPE.ACTIVE_STAY]: visitorMessages.activeStayFull,
    [PAYMENT_ITEM_TYPE.PAST_STAY]: visitorMessages.pastStay,
    [PAYMENT_ITEM_TYPE.SUBSCRIPTION]: statisticMessages.subscriptions,
    [PAYMENT_ITEM_TYPE.RESERVATION]: statisticMessages.reservations,
};

export const paymentSourceTranslationMap: Record<PAYMENT_SOURCE_TYPE, { id: string; defaultMessage: string }> = {
    [PAYMENT_SOURCE_TYPE.PAY_STATION]: paymentsMessages.payStation,
    [PAYMENT_SOURCE_TYPE.ONLINE]: paymentsMessages.online,
    [PAYMENT_SOURCE_TYPE.OPERATOR]: paymentsMessages.operator,
};

export const payStationTypeBadgeOptions: Record<PayStationOperationType, BadgeOption> = {
    [PayStationOperationType.MANUAL_ACCEPT]: {
        label: paymentsMessages.manualAccept,
        theme: 'yellow',
    },
    [PayStationOperationType.MANUAL_DISPENSE_BANKNOTES]: {
        label: paymentsMessages.manualDispenseBanknotes,
        theme: 'green',
    },
    [PayStationOperationType.MANUAL_DISPENSE_COINS]: {
        label: paymentsMessages.manualDispenseCoins,
        theme: 'emerald',
    },
    [PayStationOperationType.SYNCHRONISATION]: {
        label: paymentsMessages.synchronisation,
        theme: 'red',
    },
    [PayStationOperationType.EMPTY_RECYCLE_BOXES]: {
        label: paymentsMessages.emptyRecycleBoxes,
        theme: 'blue',
    },
    [PayStationOperationType.CLEAR_COINS]: {
        label: paymentsMessages.clearCoins,
        theme: 'gray',
    },
    [PayStationOperationType.CLEAR_BANKNOTES]: {
        label: paymentsMessages.clearBanknotes,
        theme: 'slate',
    },
    [PayStationOperationType.STAY_PAYMENT_CASH]: {
        label: paymentsMessages.stayCashPayment,
        theme: 'teal',
    },
    [PayStationOperationType.STAY_PAYMENT_CARD]: {
        label: paymentsMessages.stayCardPayment,
        theme: 'orange',
    },
    [PayStationOperationType.SUBSCRIPTION_PAYMENT_CARD]: {
        label: paymentsMessages.subscriptionPaymentCard,
        theme: 'pink',
    },
    [PayStationOperationType.SUBSCRIPTION_PAYMENT_CASH]: {
        label: paymentsMessages.subscriptionPaymentCash,
        theme: 'fuchsia',
    },
};

export const payStationStatusBadgeOptions: BadgeOptions = {
    [PayStationStatus.SUCCESS]: {
        label: paymentsMessages.successful,
        theme: 'green',
    },
    [PayStationStatus.PENDING]: {
        label: paymentsMessages.pending,
        theme: 'yellow',
    },
    [PayStationStatus.CANCELED]: {
        label: paymentsMessages.canceled,
        theme: 'red',
    },
    [PayStationStatus.ERROR]: {
        label: paymentsMessages.error,
        theme: 'red',
    },
};

export const payStationDeviceOptions = {
    [PayStationDeviceType.BANKNOTE_VALIDATOR]: paymentsMessages.banknoteValidator,
    [PayStationDeviceType.COIN_SELECTOR]: paymentsMessages.coinSelector,
};

export const payStationUiModelTranslationMap: Record<PAY_STATION_UI_MODAL, { id: string; defaultMessage: string }> = {
    [PAY_STATION_UI_MODAL.INTERACTIVE]: paymentsMessages.interactive,
    [PAY_STATION_UI_MODAL.UNATTENDED]: paymentsMessages.unattended,
    [PAY_STATION_UI_MODAL.READ_ONLY_DISPLAY]: paymentsMessages.readOnlyDisplay,
};

export const pipelineProfileTranslationMap: Record<PIPELINE_PROFILE, { id: string; defaultMessage: string }> = {
    [PIPELINE_PROFILE.BG_WITH_FISCALISATION]: paymentsMessages.bgWithFiscalisation,
    [PIPELINE_PROFILE.BG_WITHOUT_FISCALISATION]: paymentsMessages.bgWithoutFiscalisation,
};

export const payStationPaymentTypeTranslationMap: Record<
    PayStationPaymentType,
    { id: string; defaultMessage: string }
> = {
    [PayStationPaymentType.CARD]: paymentsMessages.card,
    [PayStationPaymentType.CASH]: paymentsMessages.cash,
    [PayStationPaymentType.ONLINE]: paymentsMessages.online,
};

export const badgePayStationStatus: BadgeOptions = {
    [PAY_STATION_STATUS.OPERATIONAL]: {
        theme: 'green',
        icon: ICON_MAP.CHECK,
        label: paymentsMessages.operational,
    },
    [PAY_STATION_STATUS.WARNING]: {
        theme: 'yellow',
        icon: ICON_MAP.CIRCLE_EXCLAMATION,
        label: paymentsMessages.warning,
    },
    [PAY_STATION_STATUS.ERROR]: {
        theme: 'red',
        icon: ICON_MAP.DANGER,
        label: paymentsMessages.error,
    },
    [PAY_STATION_STATUS.NO_CONNECTION]: {
        theme: 'orange',
        icon: ICON_MAP.X_MARK,
        label: paymentsMessages.noConnection,
    },
};

export enum EventType {
    START_ENABLE_ACCEPTORS_COMMAND = 0,
    STOP_ENABLE_ACCEPTORS_COMMAND = 1,
    START_DISABLE_ACCEPTORS_COMMAND = 2,
    STOP_DISABLE_ACCEPTORS_COMMAND = 3,
    START_CLEAR_COINS_COMMAND = 4,
    STOP_CLEAR_COINS_COMMAND = 5,
    START_CLEAR_BANKNOTES_COMMAND = 6,
    STOP_CLEAR_BANKNOTES_COMMAND = 7,
    START_RETURN_BY_NOMINAL_COMMAND = 8,
    STOP_RETURN_BY_NOMINAL_COMMAND = 9,
    START_EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_COMMAND = 10,
    STOP_EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_COMMAND = 11,
    START_ACCEPT_AMOUNT_COMMAND = 12,
    STOP_ACCEPT_AMOUNT_COMMAND = 13,
    START_RETURN_AMOUNT_COMMAND = 14,
    STOP_RETURN_AMOUNT_COMMAND = 15,
    START_TIMEOUT_STOP_COMMAND = 16,
    STOP_TIMEOUT_STOP_COMMAND = 17,
    START_UPDATE_SETTINGS_COMMAND = 18,
    STOP_UPDATE_SETTINGS_COMMAND = 19,
    START_GET_VALUES_IN_DEVICES_MEMORY_COMMAND = 20,
    STOP_GET_VALUES_IN_DEVICES_MEMORY_COMMAND = 21,
    START_SYNCHRONIZATION_COMMAND = 22,
    STOP_SYNCHRONIZATION_COMMAND = 23,
    ACCEPT_AMOUNT_STOPPED_BY_TIMER_COMMAND = 24,
    AMOUNT_ACCEPTED = 25,
    AMOUNT_DISPENSED = 26,
    REMAINING_AMOUNT_FOR_RETURN = 27,
    BANKNOTE_COLLECTED_FROM_RECYCLE_BOX = 28,
    BANKNOTE_COLLECTED_BY_STOP_TIMER = 29,
    DIFF_BETWEEN_IN_MEMORY_AND_DEVICE_NOMINAL_VALUES_COIN_PAYOUT_EXCEPTION = 30,
    DIFF_BETWEEN_IN_MEMORY_AND_DEVICE_NOMINAL_VALUES_BANKNOTE_VALIDATOR_EXCEPTION = 31,
    INCOMPLETE_DISPENSE = 32,
    UNPAID_COINS_AMOUNT_EXCEPTION = 33,
    OVERPAID_AMOUNT_EXCEPTION = 34,
    RETURN_BY_NOMINAL_VALIDATION_EXCEPTION = 35,
    RETURN_BY_NOMINAL_NO_PAYOUT_DEVICE_FOUND_EXCEPTION = 36,
    CLEAR_NOMINAL_FROM_DEVICE_MEMORY_EXCEPTION = 37,
    SYNCHRONIZE_NOMINAL_EXCEPTION = 38,
    GET_COIN_SELECTOR_DEVICE_MEMORY_EXCEPTION = 39,
    GET_BANKNOTE_VALIDATOR_DEVICE_MEMORY_EXCEPTION = 40,
    OPEN_COIN_HANDLER_EXCEPTION = 41,
    CLOSE_COIN_HANDLER_EXCEPTION = 42,
    UPDATE_SETTINGS_COMMAND_EXCEPTION = 43,
    DISPENSE_BANKNOTE_EXCEPTION = 44,
    DISPENSE_COINS_EXCEPTION = 45,
    STOP_DISPENSE_VALUE_EXCEPTION = 46,
    CASH_API_BACKGROUND_SERVICE_STOPPED = 47,
    CASH_API_HEALTH_CHECK = 48,
    CASH_API_QUEUE_WORKER_STOPPED = 49,
    CASH_API_QUEUE_WORKER_STARTED = 50,
    DISABLED_BY_SETTINGS_BANKNOTE_VALIDATOR_EXCEPTION = 51,
    CLEAR_BANKNOTES_COMMAND_EXCEPTION = 52,
    EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_EXCEPTION = 53,
    STOP_EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_COMMAND_AND_SAVE_DATE = 54,
    SYNCHRONIZE_NOMINAL_CORRECTED_FROM_DEVICE = 62,
    SUCCESSFUL_PAYMENT = 101,
    CANCELLED_PAYMENT = 102,
    PROCESS_PAYMENT_EXCEPTION = 103,
    SUCCESSFUL_REVERSE = 104,
    PROCESS_REVERSE_PAYMENT_EXCEPTION = 105,
    OPERATION_EXCEPTION = 106,
    CARD_API_BACKGROUND_SERVICE_STOPPED = 107,
    CARD_API_QUEUE_WORKER_STARTED = 108,
    CARD_API_QUEUE_WORKER_STOPPED = 109,
    CARD_API_HEALTH_CHECK = 110,
    INITIALIZE_PAYMENT = 111,
    INITIALIZE_REVERSAL = 112,
    CARD_ACCEPTED = 113,
    CARD_REJECTED = 114,
    ON_PIN_STARTED = 115,
    FISCAL_API_HEALTH_CHECK = 300,
    FISCAL_API_QUEUE_WORKER_STARTED = 301,
    FISCAL_API_QUEUE_WORKER_STOPPED = 302,
    START_CREATE_FISCAL_NOTE_COMMAND = 303,
    STOP_CREATE_FISCAL_NOTE_COMMAND = 304,
    STOP_CREATE_CARD_FISCAL_NOTE_COMMAND = 305,
    START_GENERATE_DAILY_REPORT = 306,
    STOP_GENERATE_DAILY_REPORT = 307,
    CREATE_CASH_FISCAL_NOTE_EXCEPTION = 308,
    CREATE_CARD_FISCAL_NOTE_EXCEPTION = 309,
    GENERATE_DAILY_REPORT_EXCEPTION = 310,
    SEND_FISCAL_DEVICE_STATUS = 311,
    SEND_FISCAL_DEVICE_STATUS_EXCEPTION = 312,
    UNKNOWN_EVENT = 1000,
    OPERATIONAL_ERRORS_CHANGE = 1001,
    CASH_INSERT = 1002,
    CASH_DISPENSE = 1003,
    SUCCESSFUL_CARD_TRANSACTION = 1004,
    FAILED_CARD_TRANSACTION = 1005,
    SUCCESSFUL_CARD_TRANSACTION_REVERSAL = 1006,
    FAILED_CARD_TRANSACTION_REVERSAL = 1007,
    CANCEL_OPERATION = 1008,
    CORE_ERROR = 1009,
    LIST_ACTIVE_STAY = 1010,
    RETRIEVE_ACTIVE_STAY = 1011,
    CASH_INSERT_CORE_ERROR = 1012,
    CASH_DISPENSE_CORE_ERROR = 1013,
    CARD_TRANSACTION_CORE_ERROR = 1014,
    TRANSFER_BANKNOTES_FROM_RECYCLE_BOX = 1015,
    TRANSFER_BANKNOTES_FROM_RECYCLE_BOX_CORE_ERROR = 1016,
    RETRIEVE_ACTIVE_STAY_BY_IDENTIFIER = 1017,
    MANUAL_DISPENSE_REQUESTED_DENOMINATIONS = 1018,
    CASH_LEVELS_BEFORE_CLEAR = 1019,
    ISSUING_FISCAL_NOTE_COMPLETED = 1020,
    ISSUING_FISCAL_NOTE_FAILED = 1021,
}

export const eventTypeTranslationMap = {
    [EventType.START_ENABLE_ACCEPTORS_COMMAND]: eventTypeMessages.startEnableAcceptorsCommand,
    [EventType.STOP_ENABLE_ACCEPTORS_COMMAND]: eventTypeMessages.stopEnableAcceptorsCommand,
    [EventType.START_DISABLE_ACCEPTORS_COMMAND]: eventTypeMessages.startDisableAcceptorsCommand,
    [EventType.STOP_DISABLE_ACCEPTORS_COMMAND]: eventTypeMessages.stopDisableAcceptorsCommand,
    [EventType.START_CLEAR_COINS_COMMAND]: eventTypeMessages.startClearCoinsCommand,
    [EventType.STOP_CLEAR_COINS_COMMAND]: eventTypeMessages.stopClearCoinsCommand,
    [EventType.START_CLEAR_BANKNOTES_COMMAND]: eventTypeMessages.startClearBanknotesCommand,
    [EventType.STOP_CLEAR_BANKNOTES_COMMAND]: eventTypeMessages.stopClearBanknotesCommand,
    [EventType.START_RETURN_BY_NOMINAL_COMMAND]: eventTypeMessages.startReturnByNominalCommand,
    [EventType.STOP_RETURN_BY_NOMINAL_COMMAND]: eventTypeMessages.stopReturnByNominalCommand,
    [EventType.START_EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_COMMAND]:
        eventTypeMessages.startEmptyBanknoteValidatorRecycleBoxesCommand,
    [EventType.STOP_EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_COMMAND]:
        eventTypeMessages.stopEmptyBanknoteValidatorRecycleBoxesCommand,
    [EventType.START_ACCEPT_AMOUNT_COMMAND]: eventTypeMessages.startAcceptAmountCommand,
    [EventType.STOP_ACCEPT_AMOUNT_COMMAND]: eventTypeMessages.stopAcceptAmountCommand,
    [EventType.START_RETURN_AMOUNT_COMMAND]: eventTypeMessages.startReturnAmountCommand,
    [EventType.STOP_RETURN_AMOUNT_COMMAND]: eventTypeMessages.stopReturnAmountCommand,
    [EventType.START_TIMEOUT_STOP_COMMAND]: eventTypeMessages.startTimeoutStopCommand,
    [EventType.STOP_TIMEOUT_STOP_COMMAND]: eventTypeMessages.stopTimeoutStopCommand,
    [EventType.START_UPDATE_SETTINGS_COMMAND]: eventTypeMessages.startUpdateSettingsCommand,
    [EventType.STOP_UPDATE_SETTINGS_COMMAND]: eventTypeMessages.stopUpdateSettingsCommand,
    [EventType.START_GET_VALUES_IN_DEVICES_MEMORY_COMMAND]: eventTypeMessages.startGetValuesInDevicesMemoryCommand,
    [EventType.STOP_GET_VALUES_IN_DEVICES_MEMORY_COMMAND]: eventTypeMessages.stopGetValuesInDevicesMemoryCommand,
    [EventType.START_SYNCHRONIZATION_COMMAND]: eventTypeMessages.startSynchronizationCommand,
    [EventType.STOP_SYNCHRONIZATION_COMMAND]: eventTypeMessages.stopSynchronizationCommand,
    [EventType.ACCEPT_AMOUNT_STOPPED_BY_TIMER_COMMAND]: eventTypeMessages.acceptAmountStoppedByTimerCommand,
    [EventType.AMOUNT_ACCEPTED]: eventTypeMessages.amountAccepted,
    [EventType.AMOUNT_DISPENSED]: eventTypeMessages.amountDispensed,
    [EventType.REMAINING_AMOUNT_FOR_RETURN]: eventTypeMessages.remainingAmountForReturn,
    [EventType.BANKNOTE_COLLECTED_FROM_RECYCLE_BOX]: eventTypeMessages.banknoteCollectedFromRecycleBox,
    [EventType.BANKNOTE_COLLECTED_BY_STOP_TIMER]: eventTypeMessages.banknoteCollectedByStopTimer,
    [EventType.DIFF_BETWEEN_IN_MEMORY_AND_DEVICE_NOMINAL_VALUES_COIN_PAYOUT_EXCEPTION]:
        eventTypeMessages.diffBetweenInMemoryAndDeviceNominalValuesCoinPayoutException,
    [EventType.DIFF_BETWEEN_IN_MEMORY_AND_DEVICE_NOMINAL_VALUES_BANKNOTE_VALIDATOR_EXCEPTION]:
        eventTypeMessages.diffBetweenInMemoryAndDeviceNominalValuesBanknoteValidatorException,
    [EventType.INCOMPLETE_DISPENSE]: eventTypeMessages.incompleteDispense,
    [EventType.UNPAID_COINS_AMOUNT_EXCEPTION]: eventTypeMessages.unpaidCoinsAmountException,
    [EventType.OVERPAID_AMOUNT_EXCEPTION]: eventTypeMessages.overpaidAmountException,
    [EventType.RETURN_BY_NOMINAL_VALIDATION_EXCEPTION]: eventTypeMessages.returnByNominalValidationException,
    [EventType.RETURN_BY_NOMINAL_NO_PAYOUT_DEVICE_FOUND_EXCEPTION]:
        eventTypeMessages.returnByNominalNoPayoutDeviceFoundException,
    [EventType.CLEAR_NOMINAL_FROM_DEVICE_MEMORY_EXCEPTION]: eventTypeMessages.clearNominalFromDeviceMemoryException,
    [EventType.SYNCHRONIZE_NOMINAL_EXCEPTION]: eventTypeMessages.synchronizeNominalException,
    [EventType.GET_COIN_SELECTOR_DEVICE_MEMORY_EXCEPTION]: eventTypeMessages.getCoinSelectorDeviceMemoryException,
    [EventType.GET_BANKNOTE_VALIDATOR_DEVICE_MEMORY_EXCEPTION]:
        eventTypeMessages.getBanknoteValidatorDeviceMemoryException,
    [EventType.OPEN_COIN_HANDLER_EXCEPTION]: eventTypeMessages.openCoinHandlerException,
    [EventType.CLOSE_COIN_HANDLER_EXCEPTION]: eventTypeMessages.closeCoinHandlerException,
    [EventType.UPDATE_SETTINGS_COMMAND_EXCEPTION]: eventTypeMessages.updateSettingsCommandException,
    [EventType.DISPENSE_BANKNOTE_EXCEPTION]: eventTypeMessages.dispenseBanknoteException,
    [EventType.DISPENSE_COINS_EXCEPTION]: eventTypeMessages.dispenseCoinsException,
    [EventType.STOP_DISPENSE_VALUE_EXCEPTION]: eventTypeMessages.stopDispenseValueException,
    [EventType.CASH_API_BACKGROUND_SERVICE_STOPPED]: eventTypeMessages.cashApiBackgroundServiceStopped,
    [EventType.CASH_API_HEALTH_CHECK]: eventTypeMessages.cashApiHealthCheck,
    [EventType.CASH_API_QUEUE_WORKER_STOPPED]: eventTypeMessages.cashApiQueueWorkerStopped,
    [EventType.CASH_API_QUEUE_WORKER_STARTED]: eventTypeMessages.cashApiQueueWorkerStarted,
    [EventType.DISABLED_BY_SETTINGS_BANKNOTE_VALIDATOR_EXCEPTION]:
        eventTypeMessages.disabledBySettingsBanknoteValidatorException,
    [EventType.CLEAR_BANKNOTES_COMMAND_EXCEPTION]: eventTypeMessages.clearBanknotesCommandException,
    [EventType.EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_EXCEPTION]:
        eventTypeMessages.emptyBanknoteValidatorRecycleBoxesException,
    [EventType.SUCCESSFUL_PAYMENT]: eventTypeMessages.successfulPayment,
    [EventType.CANCELLED_PAYMENT]: eventTypeMessages.cancelledPayment,
    [EventType.PROCESS_PAYMENT_EXCEPTION]: eventTypeMessages.processPaymentException,
    [EventType.SUCCESSFUL_REVERSE]: eventTypeMessages.successfulReverse,
    [EventType.PROCESS_REVERSE_PAYMENT_EXCEPTION]: eventTypeMessages.processReversePaymentException,
    [EventType.OPERATION_EXCEPTION]: eventTypeMessages.operationException,
    [EventType.CARD_API_BACKGROUND_SERVICE_STOPPED]: eventTypeMessages.cardApiBackgroundServiceStopped,
    [EventType.CARD_API_QUEUE_WORKER_STARTED]: eventTypeMessages.cardApiQueueWorkerStarted,
    [EventType.CARD_API_QUEUE_WORKER_STOPPED]: eventTypeMessages.cardApiQueueWorkerStopped,
    [EventType.CARD_API_HEALTH_CHECK]: eventTypeMessages.cardApiHealthCheck,
    [EventType.INITIALIZE_PAYMENT]: eventTypeMessages.initializePayment,
    [EventType.INITIALIZE_REVERSAL]: eventTypeMessages.initializeReversal,
    [EventType.CARD_ACCEPTED]: eventTypeMessages.cardAccepted,
    [EventType.CARD_REJECTED]: eventTypeMessages.cardRejected,
    [EventType.ON_PIN_STARTED]: eventTypeMessages.cardPinCodeRequested,
    [EventType.UNKNOWN_EVENT]: eventTypeMessages.unknownEvent,
    [EventType.OPERATIONAL_ERRORS_CHANGE]: eventTypeMessages.operationalErrorsChange,
    [EventType.CASH_INSERT]: eventTypeMessages.cashInsert,
    [EventType.CASH_DISPENSE]: eventTypeMessages.cashDispense,
    [EventType.SUCCESSFUL_CARD_TRANSACTION]: eventTypeMessages.successfulCardTransaction,
    [EventType.FAILED_CARD_TRANSACTION]: eventTypeMessages.failedCardTransaction,
    [EventType.SUCCESSFUL_CARD_TRANSACTION_REVERSAL]: eventTypeMessages.successfulCardTransactionReversal,
    [EventType.FAILED_CARD_TRANSACTION_REVERSAL]: eventTypeMessages.failedCardTransactionReversal,
    [EventType.CANCEL_OPERATION]: eventTypeMessages.cancelOperation,
    [EventType.CORE_ERROR]: eventTypeMessages.coreError,
    [EventType.LIST_ACTIVE_STAY]: eventTypeMessages.listActiveStay,
    [EventType.RETRIEVE_ACTIVE_STAY]: eventTypeMessages.retrieveActiveStay,
    [EventType.CASH_INSERT_CORE_ERROR]: eventTypeMessages.cashInsertCoreError,
    [EventType.CASH_DISPENSE_CORE_ERROR]: eventTypeMessages.cashDispenseCoreError,
    [EventType.CARD_TRANSACTION_CORE_ERROR]: eventTypeMessages.cardTransactionCoreError,
    [EventType.TRANSFER_BANKNOTES_FROM_RECYCLE_BOX]: eventTypeMessages.transferBanknotesFromRecycleBox,
    [EventType.TRANSFER_BANKNOTES_FROM_RECYCLE_BOX_CORE_ERROR]:
        eventTypeMessages.transferBanknotesFromRecycleBoxCoreError,
    [EventType.STOP_EMPTY_BANKNOTE_VALIDATOR_RECYCLE_BOXES_COMMAND_AND_SAVE_DATE]:
        eventTypeMessages.stopEmptyBanknoteValidatorRecycleBoxesCommandAndSaveDate,
    [EventType.SYNCHRONIZE_NOMINAL_CORRECTED_FROM_DEVICE]: eventTypeMessages.synchronizeNominalCorrectedFromDevice,
    [EventType.RETRIEVE_ACTIVE_STAY_BY_IDENTIFIER]: eventTypeMessages.retrieveActiveStayByIdentifier,
    [EventType.MANUAL_DISPENSE_REQUESTED_DENOMINATIONS]: eventTypeMessages.manualDispenseRequestedDenominations,
    [EventType.CASH_LEVELS_BEFORE_CLEAR]: eventTypeMessages.cashLevelsBeforeClear,
    [EventType.FISCAL_API_QUEUE_WORKER_STARTED]: eventTypeMessages.fiscalApiQueueWorkerStarted,
    [EventType.FISCAL_API_HEALTH_CHECK]: eventTypeMessages.fiscalApiHealthCheck,
    [EventType.FISCAL_API_QUEUE_WORKER_STOPPED]: eventTypeMessages.fiscalApiQueueWorkerStopped,
    [EventType.START_CREATE_FISCAL_NOTE_COMMAND]: eventTypeMessages.startCreateFiscalNoteCommand,
    [EventType.STOP_CREATE_FISCAL_NOTE_COMMAND]: eventTypeMessages.stopCreateFiscalNoteCommand,
    [EventType.STOP_CREATE_CARD_FISCAL_NOTE_COMMAND]: eventTypeMessages.stopCreateCardFiscalNoteCommand,
    [EventType.START_GENERATE_DAILY_REPORT]: eventTypeMessages.startGenerateDailyReport,
    [EventType.STOP_GENERATE_DAILY_REPORT]: eventTypeMessages.stopGenerateDailyReport,
    [EventType.CREATE_CASH_FISCAL_NOTE_EXCEPTION]: eventTypeMessages.createCashFiscalNoteException,
    [EventType.CREATE_CARD_FISCAL_NOTE_EXCEPTION]: eventTypeMessages.createCardFiscalNoteException,
    [EventType.GENERATE_DAILY_REPORT_EXCEPTION]: eventTypeMessages.generateDailyReportException,
    [EventType.SEND_FISCAL_DEVICE_STATUS]: eventTypeMessages.sendFiscalDeviceStatus,
    [EventType.SEND_FISCAL_DEVICE_STATUS_EXCEPTION]: eventTypeMessages.sendFiscalDeviceStatusException,
    [EventType.ISSUING_FISCAL_NOTE_COMPLETED]: eventTypeMessages.issuingFiscalNoteCompleted,
    [EventType.ISSUING_FISCAL_NOTE_FAILED]: eventTypeMessages.issuingFiscalNoteCompleted,
};

export enum OperationalError {
    COULD_NOT_OPEN_CONNECTION_TO_BANKNOTE_VALIDATOR = 0,
    COULD_NOT_OPEN_CONNECTION_TO_COIN_SELECTOR = 1,
    COULD_NOT_OPEN_CONNECTION_TO_PAYOUT = 2,
    COULD_NOT_OPEN_CONNECTION_TO_DONGLE = 3,
    LOST_CONNECTION_TO_BANKNOTE_VALIDATOR = 4,
    LOST_CONNECTION_TO_COIN_SELECTOR = 5,
    LOST_CONNECTION_TO_DONGLE = 6,
    LOST_CONNECTION_TO_PAYOUT = 7,
    UNKNOWN_ERROR = 8,
    BANKNOTE_JAM_ERROR = 9,
    BANKNOTE_VALIDATOR_FRAUD_ATTEMPT_ERROR = 10,
    BANKNOTE_VALIDATOR_MAINTENANCE_ERROR = 11,
    FAILED_BANKNOTE_VALIDATION_ERROR = 12,
    BANKNOTE_VALIDATOR_CALIBRATION_ERROR = 13,
    BUSY = 14,
    CASHBOX_REMOVED = 15,
    COIN_JAM_ERROR = 16,
    COIN_SELECTOR_FRAUD_ATTEMPT_ERROR = 17,
    COIN_STRING_ERROR = 18,
    COIN_REJECT_ERROR = 19,
    COIN_SELECTOR_MAINTENANCE_ERROR = 20,
    COIN_INHIBIT_ERROR = 21,
    COIN_SELECTOR_CALIBRATION_ERROR = 22,
    NONE = 23,
    CASHBOX_REPLACED = 24,
    SUCCESSFUL_CONNECTION_TO_BANKNOTE_VALIDATOR = 25,
    SUCCESSFUL_CONNECTION_TO_COIN_SELECTOR = 26,
}

export const operationalErrorMap = {
    [OperationalError.COULD_NOT_OPEN_CONNECTION_TO_BANKNOTE_VALIDATOR]:
        eventOperationErrorMessages.couldNotOpenConnectionToBanknoteValidator,
    [OperationalError.COULD_NOT_OPEN_CONNECTION_TO_COIN_SELECTOR]:
        eventOperationErrorMessages.couldNotOpenConnectionToCoinSelector,
    [OperationalError.COULD_NOT_OPEN_CONNECTION_TO_PAYOUT]: eventOperationErrorMessages.couldNotOpenConnectionToPayout,
    [OperationalError.COULD_NOT_OPEN_CONNECTION_TO_DONGLE]: eventOperationErrorMessages.couldNotOpenConnectionToDongle,
    [OperationalError.LOST_CONNECTION_TO_BANKNOTE_VALIDATOR]:
        eventOperationErrorMessages.lostConnectionToBanknoteValidator,
    [OperationalError.LOST_CONNECTION_TO_COIN_SELECTOR]: eventOperationErrorMessages.lostConnectionToCoinSelector,
    [OperationalError.LOST_CONNECTION_TO_DONGLE]: eventOperationErrorMessages.lostConnectionToDongle,
    [OperationalError.LOST_CONNECTION_TO_PAYOUT]: eventOperationErrorMessages.lostConnectionToPayout,
    [OperationalError.UNKNOWN_ERROR]: eventOperationErrorMessages.unknownError,
    [OperationalError.BANKNOTE_JAM_ERROR]: eventOperationErrorMessages.banknoteJamError,
    [OperationalError.BANKNOTE_VALIDATOR_FRAUD_ATTEMPT_ERROR]:
        eventOperationErrorMessages.banknoteValidatorFraudAttemptError,
    [OperationalError.BANKNOTE_VALIDATOR_MAINTENANCE_ERROR]:
        eventOperationErrorMessages.banknoteValidatorMaintenanceError,
    [OperationalError.FAILED_BANKNOTE_VALIDATION_ERROR]: eventOperationErrorMessages.failedBanknoteValidationError,
    [OperationalError.BANKNOTE_VALIDATOR_CALIBRATION_ERROR]:
        eventOperationErrorMessages.banknoteValidatorCalibrationError,
    [OperationalError.BUSY]: eventOperationErrorMessages.busy,
    [OperationalError.CASHBOX_REMOVED]: eventOperationErrorMessages.cashboxRemoved,
    [OperationalError.COIN_JAM_ERROR]: eventOperationErrorMessages.coinJamError,
    [OperationalError.COIN_SELECTOR_FRAUD_ATTEMPT_ERROR]: eventOperationErrorMessages.coinSelectorFraudAttemptError,
    [OperationalError.COIN_STRING_ERROR]: eventOperationErrorMessages.coinStringError,
    [OperationalError.COIN_REJECT_ERROR]: eventOperationErrorMessages.coinRejectError,
    [OperationalError.COIN_SELECTOR_MAINTENANCE_ERROR]: eventOperationErrorMessages.coinSelectorMaintenanceError,
    [OperationalError.COIN_INHIBIT_ERROR]: eventOperationErrorMessages.coinInhibitError,
    [OperationalError.COIN_SELECTOR_CALIBRATION_ERROR]: eventOperationErrorMessages.coinSelectorCalibrationError,
    [OperationalError.NONE]: eventOperationErrorMessages.none,
    [OperationalError.CASHBOX_REPLACED]: eventOperationErrorMessages.cashboxReplaced,
    [OperationalError.SUCCESSFUL_CONNECTION_TO_BANKNOTE_VALIDATOR]:
        eventOperationErrorMessages.successfulConnectionToBanknoteValidator,
    [OperationalError.SUCCESSFUL_CONNECTION_TO_COIN_SELECTOR]:
        eventOperationErrorMessages.successfulConnectionToCoinSelector,
};

export const eventCategoryBadgeOptions: BadgeOptions = {
    [EventCategory.EVENT]: {
        label: paymentsMessages.eventLog,
        theme: 'gray',
        icon: ICON_MAP.STATIC_SPINNER,
    },
    [EventCategory.ERROR]: {
        label: paymentsMessages.error,
        theme: 'red',
        icon: ICON_MAP.X_MARK,
    },
};

export const eventAPIBadgeOptions: BadgeOptions = {
    [PayStationEventAPI.CARD_API]: {
        label: paymentsMessages.cardAPI,
        theme: 'blue',
    },
    [PayStationEventAPI.FISCAL_API]: {
        label: paymentsMessages.fiscalAPI,
        theme: 'yellow',
    },
    [PayStationEventAPI.CASH_API]: {
        label: paymentsMessages.cashAPI,
        theme: 'green',
    },
    [PayStationEventAPI.BACKEND]: {
        label: paymentsMessages.backend,
        theme: 'red',
    },
};

export enum CashTransactionType {
    INSERT = 'insert',
    DISPENSE = 'dispense',
    SYNCHRONISATION = 'synchronisation',
    CLEAR = 'clear',
}

export enum CardTransactionType {
    SUCCESS = 'success',
    FAIL = 'fail',
    REVERSE = 'reverse',
    REVERSE_FAIL = 'reverse-fail',
    CANCEL = 'cancel',
}

export const badgeOptionsCashTransaction: BadgeOptions = {
    [CashTransactionType.INSERT]: {
        label: paymentsMessages.insert,
        theme: 'green',
    },
    [CashTransactionType.DISPENSE]: {
        label: paymentsMessages.dispense,
        theme: 'blue',
    },
    [CashTransactionType.SYNCHRONISATION]: {
        label: paymentsMessages.synchronisation,
        theme: 'yellow',
    },
    [CashTransactionType.CLEAR]: {
        label: paymentsMessages.clear,
        theme: 'red',
    },
};

export const badgeOptionsCardTransaction = {
    [CardTransactionType.CANCEL]: {
        label: paymentsMessages.cancel,
        theme: 'blue',
    },
    [CardTransactionType.FAIL]: {
        label: paymentsMessages.fail,
        theme: 'red',
    },
    [CardTransactionType.SUCCESS]: {
        label: paymentsMessages.successful,
        theme: 'green',
    },
    [CardTransactionType.REVERSE]: {
        label: paymentsMessages.reverse,
        theme: 'yellow',
    },
    [CardTransactionType.REVERSE_FAIL]: {
        label: paymentsMessages.reverseFail,
        theme: 'orange',
    },
};

export const paymentServiceTypeTranslationMap: Record<PAYMENT_SERVICE_TYPE, { id: string; defaultMessage: string }> = {
    [PAYMENT_SERVICE_TYPE.PARKING]: paymentsMessages.parking,
    [PAYMENT_SERVICE_TYPE.SUBSCRIPTION]: paymentsMessages.subscription,
};

export const CANVAS_SHORT_WIDTH = 300;
export const CANVAS_LONG_WIDTH = 386;
export const CANVAS_LINE_HEIGHT = 18;
